import React from 'react';
const { Icon, Logo, Button, StatusPill, Card, Avatar, BarMini, Donut, Tabs, IndiceBadge } = window;
/* ============================================================
   BOOKER — Tela: Lista / tabela de contratos
   ============================================================ */
var { useState, useEffect, useRef, useMemo } = React;

/* Select dos filtros — FORA do componente: dentro, o React recria o tipo a cada
   render e remonta o <select>, fazendo o filtro "não obedecer". */
const Sel = ({ value, onChange, children, w }) => (
  <div style={{ position: 'relative', display: 'inline-block' }}>
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      appearance: 'none', padding: '9px 32px 9px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-strong)',
      background: 'var(--surface)', color: 'var(--text-strong)', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', minWidth: w || 'auto' }}>
      {children}
    </select>
    <Icon name="chevronD" size={14} style={{ position: 'absolute', right: 11, top: 11, pointerEvents: 'none', color: 'var(--text-muted)' }} />
  </div>
);

function Contratos({ onNavigate, initialFilter, sync }) {
  const DB = window.DB;
  const { fmtBRL, fmtData } = DB;
  const todos = useMemo(() => DB.contratosComCliente(), [sync]);

  const [busca, setBusca] = useState('');
  const [fStatus, setFStatus] = useState(initialFilter?.status || 'all');
  const [fIndice, setFIndice] = useState('all');
  const [fMes, setFMes] = useState('all');
  const [sort, setSort] = useState({ col: 'venc', dir: 'asc' });

  const MES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const mesKey = (d) => { const x = DB.parseData(d); return isNaN(x.getTime()) ? '' : `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`; };
  const mesesDisp = useMemo(() => {
    const m = new Map();
    todos.forEach(k => { const x = DB.parseData(k.dataVencimento); if (!isNaN(x.getTime())) m.set(mesKey(k.dataVencimento), `${MES[x.getMonth()]}/${x.getFullYear()}`); });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [todos]);

  const filtrados = useMemo(() => {
    let r = todos.filter(k => {
      if (fStatus === 'avencer') { if (k.status === 'Encerrado' || !(k.dias >= 0 && k.dias <= 30)) return false; }
      else if (fStatus !== 'all' && k.status !== fStatus) return false;
      if (fIndice !== 'all' && k.indice !== fIndice) return false;
      if (fMes !== 'all' && mesKey(k.dataVencimento) !== fMes) return false;
      if (busca) {
        const q = busca.toLowerCase();
        const qd = q.replace(/\D/g, '');   // só dígitos (p/ busca por CNPJ)
        // também casa pelos participantes do rateio (contrato de grupo aparece
        // ao buscar o nome/CNPJ de QUALQUER empresa que participa dele)
        const emParticipantes = k.participantesBusca && (k.participantesBusca.includes(q) || (qd.length >= 3 && k.participantesBusca.includes(qd)));
        if (!(k.codigo.toLowerCase().includes(q) || k.cliente.razao.toLowerCase().includes(q) ||
          k.cliente.fantasia.toLowerCase().includes(q) || k.objeto.toLowerCase().includes(q) || k.cliente.cnpj.includes(q) || emParticipantes)) return false;
      }
      return true;
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    r.sort((a, b) => {
      let va, vb;
      if (sort.col === 'venc') { va = DB.parseData(a.dataVencimento); vb = DB.parseData(b.dataVencimento); }
      else if (sort.col === 'valor') { va = a.valorAtual; vb = b.valorAtual; }
      else if (sort.col === 'cliente') { va = a.cliente.fantasia; vb = b.cliente.fantasia; }
      else { va = a.codigo; vb = b.codigo; }
      return va < vb ? -dir : va > vb ? dir : 0;
    });
    return r;
  }, [todos, busca, fStatus, fIndice, fMes, sort]);

  const Selo = ({ cor, bg, children }) => (
    <span style={{ fontSize: 9.5, fontWeight: 800, color: cor, background: bg, padding: '2px 6px', borderRadius: 5, letterSpacing: '.03em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{children}</span>
  );

  const toggleSort = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));

  const Th = ({ col, children, align = 'left', w }) => (
    <th onClick={col ? () => toggleSort(col) : undefined} style={{ textAlign: align, padding: '0 16px 12px', fontSize: 11.5, fontWeight: 700,
      color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', cursor: col ? 'pointer' : 'default', whiteSpace: 'nowrap', width: w }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        {children}
        {col && sort.col === col && <Icon name={sort.dir === 'asc' ? 'arrowUp' : 'arrowDown'} size={13} />}
      </span>
    </th>
  );

  return (
    <div style={{ animation: 'fadeUp .4s ease both' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-.02em' }}>Gestão de Contratos</h1>
          <p style={{ margin: '5px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>{filtrados.length} de {todos.length} contratos · dados extraídos dos documentos originais</p>
        </div>
        <Button variant="default" icon="download" onClick={() => window.Api.baixarArquivo('/api/contracts/export', 'contratos_booker.xlsx')}>Exportar</Button>
      </div>

      {/* Filtros */}
      <Card pad={14} style={{ marginBottom: 16 }}>
        {/* Linha de cima: filtros */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <Sel value={fStatus} onChange={setFStatus}>
            <option value="all">Status: todos</option>
            <option value="Ativo">Ativo</option><option value="avencer">A renovar (30d)</option>
            <option value="Encerrado">Encerrado</option>
          </Sel>
          <Sel value={fIndice} onChange={setFIndice}>
            <option value="all">Índice: todos</option>
            {Object.keys(DB.INDICES).map(k => <option key={k} value={k}>{DB.INDICES[k].label}</option>)}
          </Sel>
          <Sel value={fMes} onChange={setFMes} w={160}>
            <option value="all">Mês venc.: todos</option>
            {mesesDisp.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </Sel>
        </div>
        {/* Linha de baixo: busca */}
        <div style={{ position: 'relative' }}>
          <Icon name="search" size={17} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text-muted)' }} />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por cliente, CNPJ, código ou objeto…"
            style={{ width: '100%', padding: '10px 12px 10px 38px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-strong)',
              background: 'var(--surface-2)', color: 'var(--text-strong)', fontSize: 14, outline: 'none' }} />
        </div>
      </Card>

      {/* Tabela */}
      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
            <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ width: 0 }}></th>
              <Th col="codigo">Contrato</Th>
              <Th col="cliente">Cliente</Th>
              <Th col="valor" align="right">Valor atual</Th>
              <Th>Índice</Th>
              <Th col="venc">Vencimento</Th>
              <Th>Status</Th>
              <th style={{ width: 0 }}></th>
            </tr></thead>
            <tbody>
              {filtrados.map((k, i) => {
                const encerrado = k.status === 'Encerrado';
                const venc = !encerrado && k.dias >= 0 && k.dias <= 30;
                return (
                  <tr key={k.id} onClick={() => onNavigate('detalhe', k.id)}
                    style={{ borderTop: '1px solid var(--border)', cursor: 'pointer', transition: 'background .14s', animation: `fadeIn .3s ${i * 0.02}s both` }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '14px 0 14px 18px' }}><span style={{ width: 4, height: 38, borderRadius: 4, display: 'block',
                      background: k.status === 'Vencido' ? 'var(--danger)' : venc ? 'var(--booker-orange)' : k.status === 'Ativo' ? 'var(--ok)' : 'var(--border-strong)' }} /></td>
                    <td style={{ padding: '14px 16px' }}>
                      <div className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{k.codigo}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.objeto}</div>
                      {(k.grupo || k.revisar) && (
                        <div style={{ display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap' }}>
                          {k.grupo && <Selo cor="var(--booker-orange)" bg="var(--warn-bg)">Grupo</Selo>}
                          {k.revisar && <Selo cor="var(--danger)" bg="var(--danger-bg)">A revisar</Selo>}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar name={k.cliente.fantasia} size={32} />
                        <div>
                          <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>{k.cliente.fantasia}</div>
                          <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{k.cliente.cnpj}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <div className="tnum" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>{fmtBRL(k.valorAtual)}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>inicial {fmtBRL(k.valorInicial)}</div>
                    </td>
                    <td style={{ padding: '14px 16px' }}><IndiceBadge k={k.indice} /></td>
                    <td style={{ padding: '14px 16px' }}>
                      {encerrado ? (
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)' }}>Encerrado{k.encerradoEm ? ` · ${fmtData(k.encerradoEm)}` : ''}</div>
                      ) : (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>{fmtData(k.dataVencimento)}</div>
                          <div style={{ fontSize: 11.5, fontWeight: 600, color: venc ? 'var(--booker-orange)' : 'var(--text-muted)' }}>renova em {k.dias}d</div>
                        </>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px' }}><StatusPill status={venc && k.status === 'Ativo' ? 'A renovar' : k.status} /></td>
                    <td style={{ padding: '14px 18px 14px 0' }}><Icon name="chevronR" size={17} style={{ color: 'var(--text-faint)' }} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!filtrados.length && <div style={{ padding: 50, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum contrato encontrado para os filtros.</div>}
      </Card>
    </div>
  );
}

Object.assign(window, { Contratos });
